import ErrorReporter from '#/lib/ErrorReporter';
import { RewardPointsStore } from '#/stores/RewardPointsStore';
import { JucyCountryCode } from '@jucy/rentals-common';
import { makeAutoObservable } from 'mobx';
import { MutableKeys } from 'utility-types';
import { Hydratable } from '../lib/Hydratable';
import { PropsOf } from '../lib/TSUtils';
import { Brand } from '../lib/brand';
import AppStateStore from './AppStateStore';
import AuthStore from './AuthStore';
import NotificationStore from './NotificationStore';
import RentalBranchStore from './RentalBranchStore';
import SelfServiceStore from './SelfServiceStore';
import TranslationStore from './TranslationStore';

export default class RootStore {
    authStore: AuthStore = new AuthStore();
    translationStore: TranslationStore = new TranslationStore();
    rentalBranchStore: RentalBranchStore = new RentalBranchStore();
    appStateStore: AppStateStore = new AppStateStore({
        authStore: this.authStore,
        translationStore: this.translationStore,
        rentalBranchStore: this.rentalBranchStore,
    });
    selfServiceStore: SelfServiceStore = new SelfServiceStore({
        appStateStore: this.appStateStore,
    });
    notificationStore: NotificationStore = new NotificationStore();
    rewardPointsStore: RewardPointsStore = new RewardPointsStore({
        appStateStore: this.appStateStore,
    });
    brand: Brand = 'star';

    constructor(initialData?: RootStoreProps) {
        makeAutoObservable(this);
        this.hydrate(initialData);
        this.rentalBranchStore.loadSites().catch((e) => {
            ErrorReporter.captureError(e);
        });
    }

    hydrate(data?: RootStoreProps): void {
        if (data) {
            for (const prop of Object.keys(data) as MutableKeys<RootStoreProps>[]) {
                if (typeof (this[prop] as Hydratable).hydrate === 'function') {
                    (this[prop] as Hydratable).hydrate(data[prop]);
                } else {
                    this[prop] = data[prop] as never;
                }
            }
        }
    }

    get brandWebsite(): string {
        if (this.brand === 'jucy') {
            return 'https://www.jucy.com';
        }
        return 'https://www.starrv.com';
    }

    get brandLabel(): string {
        if (this.brand === 'star') {
            return 'Star RV';
        }
        return 'JUCY';
    }

    getBrandContactUsLink(region: JucyCountryCode = JucyCountryCode.nz): string {
        if (this.brand === 'star') {
            return `https://www.starrv.com/${region}/contact`;
        }
        return `https://www.jucy.com/${region}/en/about-us/help-and-contact`;
    }
}

export type RootStoreProps = Partial<PropsOf<RootStore>>;
